import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getAvatarColor, getInitials } from '../../Helpers/common';

const AdminCallConversion = () => {
  const navigate = useNavigate();
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingConversion, setEditingConversion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingConversion, setDeletingConversion] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusConversion, setStatusConversion] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    contactName: '',
    contactNumber: '',
    contactEmail: '',
    contactRemark: '',
    contactSource: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    dateRange: [null, null]
  });

  // Fetch conversions
   const fetchConversions = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      if (filters.search) {
        params.append('search', filters.search);
      }
      if(filters.dateRange[0]){
          params.append('startDate', moment(filters.dateRange[0]).format('YYYY-MM-DD'));
      }
      else if (filters.dateRange[0] && filters.dateRange[1]) {
        params.append('startDate', moment(filters.dateRange[0]).format('YYYY-MM-DD'));
        params.append('endDate', moment(filters.dateRange[1]).format('YYYY-MM-DD'));
      }
      console.log("Fetching conversions with params:", params.toString());
      const response = await axios.get(`/call-conversions?${params}`);
      
      if (response.data && response.data.data) {
        setConversions(response.data.data || []);
        // Since the API doesn't return pagination info, calculate it based on the response
        const totalItems = response.data.data.length;
        setPagination({
          currentPage: page,
          totalPages: Math.ceil(totalItems / pagination.limit),
          totalItems: totalItems,
          limit: pagination.limit
        });
      }
    } catch (error) {
      console.error('Error fetching conversions:', error);
      toast.error('Failed to fetch call conversions');
    } finally {
      setLoading(false);
    }
  };
  const handleConversionStatus = async(id,status)=>{
    try {
      setLoading(true);
      await axios.put(`/call-conversions/${id}/status`, { status });
      toast.success(`Call conversion ${status ? 'approved' : 'pending'} successfully`);
      setShowStatusModal(false);
      setStatusConversion(null);
      fetchConversions();
    } catch (error) {
      console.error('Error updating conversion status:', error);
      toast.error('Failed to update call conversion status');
    } finally {
      setLoading(false);
    }
  }

  // Show status confirmation
  const showStatusConfirmation = (conversion) => {
    setStatusConversion(conversion);
    setShowStatusModal(true);
  };
  // ...existing code...

  // Delete conversion
  const handleDeleteConversion = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`/call-conversions/${id}`);
      toast.success('Call conversion deleted successfully');
      setShowDeleteModal(false);
      setDeletingConversion(null);
      fetchConversions();
    } catch (error) {
      console.error('Error deleting conversion:', error);
      toast.error('Failed to delete call conversion');
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation
  const showDeleteConfirmation = (conversion) => {
    setDeletingConversion(conversion);
    setShowDeleteModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      contactName: '',
      contactNumber: '',
      contactEmail: '',
      contactRemark: '',
      contactSource: ''
    });
  };

  // Handle edit
  const handleEdit = (conversion) => {
    setEditingConversion(conversion);
    setFormData({
      contactName: conversion.contactName || '',
      contactNumber: conversion.contactNumber || '',
      contactEmail: conversion.contactEmail || '',
      contactRemark: conversion.contactRemark || '',
      contactSource: conversion.contactSource || ''
    });
    setShowAddForm(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchConversions(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchConversions(page);
  };

  useEffect(() => {
    fetchConversions();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Call Conversions</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your call conversion records</p>
        </div>
       
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, number, or email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <DatePicker
              selectsRange={true}
              startDate={filters.dateRange[0]}
              endDate={filters.dateRange[1]}
              onChange={(update) => setFilters(prev => ({ ...prev, dateRange: update }))}
              placeholderText="Select date range"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              isClearable={true}
            />
            <button
              type="submit"
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Add/Edit Form Modal */}
      {/* ...existing code... (modal removed) */}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingConversion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Call Conversion
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete the conversion for{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {deletingConversion.contactName}
                </span>
                {' '}({deletingConversion.contactNumber})?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingConversion(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteConversion(deletingConversion.id)}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {showStatusModal && statusConversion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Update Conversion Status
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Change the approval status of this conversion.
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to change the status of{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {statusConversion.contactName}
                </span>
                {' '}({statusConversion.contactNumber}) to{' '}
                <span className={`font-semibold ${
                  !statusConversion.status 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-orange-600 dark:text-orange-400'
                }`}>
                  {!statusConversion.status ? 'Approved' : 'Pending'}
                </span>?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusConversion(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConversionStatus(statusConversion.id, !statusConversion.status)}
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center text-white ${
                  !statusConversion.status 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  `Change to ${!statusConversion.status ? 'Approved' : 'Pending'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Counselor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Remarks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : conversions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No call conversions found
                  </td>
                </tr>
              ) : (
                conversions.map((conversion) => (
                  <tr key={conversion.id} onClick={()=>showStatusConfirmation(conversion)} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                     <td className="x-6 py-4 whitespace-nowrap">
                    <div className="flex items-center pl-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getAvatarColor('blue')}`}>
                        <span className="text-sm font-semibold">{getInitials(conversion.employee.name)}</span>
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{conversion.employee.name}</span>
                    </div>
                  </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="">
                        <div className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600">
                          {conversion.contactName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {conversion.contactNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {conversion.contactEmail || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {conversion.contactSource || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        conversion.status 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {conversion.status ? 'Approve' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {conversion.contactRemark || 'No remarks'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {conversion.createAt ? moment(conversion.createAt).format('MMM DD, YYYY') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                 
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          showDeleteConfirmation(conversion);
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.currentPage - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * pagination.limit, pagination.totalItems)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{pagination.totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.currentPage
                          ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-600 dark:text-blue-200'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCallConversion;
